import { useEffect, useMemo, useState } from "react";
import { CustomTable } from "../../componentes/table/CustomTable";
import DateMasked from "../../componentes/inputMask/DateMasked";
import Modal from "../../componentes/modal/modal";
import SapServiceRetornoEntrega from "../../services/resource/SapServiceRetornoEntrega";
import { formatLocalDate } from "../../utils/formatDate";
import Input from "../../componentes/input/input";
import { Checkbox } from "primereact/checkbox";

const inputClassName = "w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none transition-colors duration-150 placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/15";

const formatDate = (value) => value ? formatLocalDate(value) : "—";

function toIsoDate(value) {
    if (!value || typeof value !== "string") return "";
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
}

export default function SapRetornoPage() {
    const service = useMemo(() => new SapServiceRetornoEntrega(), []);

    const [lista, setLista] = useState([]);
    const [registro, setRegistro] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filtro, setFiltro] = useState(() => {
        const hoje = new Date();
        const dataInicial = new Date();
        dataInicial.setDate(hoje.getDate() - 30);

        const formatar = (data) => data.toISOString().split("T")[0];

        return {
            dataInicial: formatar(dataInicial),
            dataFinal: formatar(hoje),
            transporte: ""
        };
    });

    async function buscar(e) {
        e.preventDefault();
        carregarRetornos();
    }

    async function carregarRetornos() {
        try {
            const response = await service.listarRetornos(filtro);
            setLista(response.data ?? []);
        } finally {
            setLoading(false);
        }
    }

    async function salvar() {
        if (!registro) return;

        setLoading(true);
        try {
            await service.editar(registro);
            setRegistro(null);
            carregarRetornos();
        } finally {
            setLoading(false);
        }
    }

    function selecionar(item) {
        setRegistro({ ...item });
    }

    function alterarCampo(campo, valor) {
        setRegistro((atual) => ({ ...atual, [campo]: valor }));
    }

    const columns = useMemo(() => [
        {
            key: "filialNome",
            label: "Filial",
            sortable: true,
        },
        {
            key: "dataEntrega",
            label: "Data da entrega",
            sortable: true,
            render: (value) => formatDate(value),
        },
        {
            key: "dataCriacao",
            label: "Data de Criação",
            sortable: true,
            render: (value) => formatDate(value),
        },
        { key: "transporte", label: "Transporte", sortable: true },
        { key: "carga", label: "Carga", sortable: true, className: "font-medium" },
        {
            key: "caixasUnitarias",
            label: "CX Unit.",
            sortable: true,
        },
        {
            key: "cubos",
            label: "Cubos",
            sortable: true,
        },
        {
            key: "clienteFantasia",
            label: "Cliente",
            sortable: true,
        },
        {
            key: "editadoManualmente",
            label: "Bloqueado",
            sortable: true,
            render: (value) => (
                <div>
                    {value === true && <span className="bg-amber-500/10 text-amber-700 px-2 py-1 rounded-full">Bloqueado</span>}
                </div>
            ),
        },
        {
            key: "acoes",
            label: "Ações",
            sortable: false,
            filterable: false,
            noSelected: true,
            render: (_, item) => (
                <button
                    type="button"
                    onClick={() => selecionar(item)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-2.5 py-1.5 text-[10px] font-semibold text-primary transition-colors duration-150 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                    <i className="fa fa-pencil" /> Editar
                </button>
            ),
        }
    ], []);

    return (
        <div>
            <main className="space-y-4 pt-3">
                <section>
                    <form onSubmit={buscar} className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-2 xl:grid-cols-[minmax(160px,1fr)_minmax(160px,1fr)_minmax(180px,1fr)_auto] xl:items-end">
                        <DateMasked
                            id="data-inicial"
                            label="Data inicial"
                            value={filtro.dataInicial}
                            onChange={(dataInicial) => setFiltro((atual) => ({ ...atual, dataInicial }))}
                        />
                        <DateMasked
                            id="data-final"
                            label="Data final"
                            value={filtro.dataFinal}
                            onChange={(dataFinal) => setFiltro((atual) => ({ ...atual, dataFinal }))}
                        />
                        <Input
                            className={inputClassName}
                            label="Transporte"
                            value={filtro.transporte}
                            onChange={(event) => setFiltro((atual) => ({ ...atual, transporte: event.target.value }))}
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-xs transition-all duration-150 hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <i className={loading ? "fa fa-spinner fa-spin" : "fa fa-search"} /> Buscar
                        </button>
                    </form>

                    <CustomTable
                        data={lista}
                        columns={columns}
                        loading={loading}
                        pagination
                        pageSize={20}
                        exportable
                        showHeader={false}
                        exportFilename="Retorno SAP"
                        className="text-[11px]"
                    />
                </section>

                <Modal
                    isOpen={Boolean(registro)}
                    onClose={() => setRegistro(null)}
                    title="Editar retorno"
                    subtitle="Altere os campos necessários e salve as atualizações do retorno."
                    className="w-[92%]"
                    footer={
                        <div className="flex items-center justify-between">

                            <div className="flex gap-1">

                                <Checkbox
                                    inputId="editadoManualmente"
                                    checked={registro?.editadoManualmente}
                                    onChange={(e) => alterarCampo("editadoManualmente", e.checked)}
                                />
                                <label htmlFor="editadoManualmente" className="mr-5">
                                    Bloqueia da importação
                                </label>

                                <Checkbox
                                    inputId="escalaVinculada"
                                    disabled
                                    checked={registro?.escalaVinculada}
                                    onChange={(e) => alterarCampo("escalaVinculada", e.checked)}
                                />
                                <label htmlFor="escalaVinculada">
                                    Escala vinculada
                                </label>

                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setRegistro(null)}
                                    className="rounded-lg px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors duration-150"
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="button"
                                    onClick={salvar}
                                    disabled={loading}
                                    className="inline-flex items-center gap-2 rounded-lg bg-primary hover:brightness-95 px-4 py-2 text-sm font-semibold text-primary-foreground transition-all duration-150"
                                >
                                    <i className={loading ? "fa fa-spinner fa-spin" : "fa fa-save"} />
                                    Salvar alterações
                                </button>
                            </div>

                        </div>
                    }
                >
                    {registro && (
                        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">

                            <Input
                                label="Filial"
                                value={registro.filialNome ?? ""}
                                disabled
                            />

                            <DateMasked
                                label="Data Entrega"
                                value={toIsoDate(registro.dataEntrega)}
                                onChange={(v) => alterarCampo("dataEntrega", v)}
                            />

                            <DateMasked
                                label="Data Criação"
                                value={toIsoDate(registro.dataCriacao)}
                                onChange={(v) => alterarCampo("dataCriacao", v)}
                            />

                            <DateMasked
                                label="Data Retorno"
                                value={toIsoDate(registro.dataRetorno)}
                                onChange={(v) => alterarCampo("dataRetorno", v)}
                            />

                            <Input
                                label="Transporte"
                                value={registro.transporte ?? ""}
                                onChange={(e) => alterarCampo("transporte", e.target.value)}
                                disabled
                            />

                            <Input
                                label="Código Cliente"
                                value={registro.clienteCodigo ?? ""}
                                onChange={(e) => alterarCampo("clienteCodigo", e.target.value)}
                                disabled
                            />

                            <Input
                                label="Razão Social"
                                value={registro.clienteRazaoSocial ?? ""}
                                onChange={(e) => alterarCampo("clienteRazaoSocial", e.target.value)}
                                disabled
                            />

                            <Input
                                label="Fantasia"
                                value={registro.clienteFantasia ?? ""}
                                onChange={(e) => alterarCampo("clienteFantasia", e.target.value)}
                                disabled
                            />

                            <Input
                                label="Código Vendedor"
                                value={registro.codigoVendedor ?? ""}
                                onChange={(e) => alterarCampo("codigoVendedor", e.target.value)}
                                disabled
                            />

                            <Input
                                label="Carga"
                                value={registro.carga ?? ""}
                                onChange={(e) => alterarCampo("carga", e.target.value)}
                            />

                            <Input
                                label="Carga Entrega ID"
                                value={registro.cargaEntregaId ?? ""}
                                onChange={(e) => alterarCampo("cargaEntregaId", e.target.value)}
                                disabled
                            />

                            <Input
                                label="Motorista"
                                value={registro.motoristaCodigo ?? ""}
                                onChange={(e) => alterarCampo("motoristaCodigo", e.target.value)}
                            />

                            <Input
                                label="Veículo"
                                value={registro.veiculoCodigo ?? ""}
                                onChange={(e) => alterarCampo("veiculoCodigo", e.target.value)}
                            />

                            <Input
                                label="Canal"
                                value={registro.canal ?? ""}
                                onChange={(e) => alterarCampo("canal", e.target.value)}
                            />

                            <Input
                                label="Produto ID"
                                value={registro.produtoId ?? ""}
                                onChange={(e) => alterarCampo("produtoId", e.target.value)}
                                disabled
                            />

                            <Input
                                label="Produto"
                                value={registro.produtoNome ?? ""}
                                onChange={(e) => alterarCampo("produtoNome", e.target.value)}
                                disabled />

                            <Input
                                label="Nota Fiscal"
                                value={registro.notaFiscal ?? ""}
                                onChange={(e) => alterarCampo("notaFiscal", e.target.value)}
                            />

                            <Input
                                label="Motivo Código"
                                value={registro.motivoCodigo ?? ""}
                                onChange={(e) => alterarCampo("motivoCodigo", e.target.value)}
                            />

                            <Input
                                label="Motivo"
                                value={registro.motivoNome ?? ""}
                                onChange={(e) => alterarCampo("motivoNome", e.target.value)}
                            />

                            <Input
                                label="Caixas Físicas"
                                value={registro.caixasFisicas ?? ""}
                                onChange={(e) => alterarCampo("caixasFisicas", e.target.value)}
                            />

                            <Input
                                label="Caixas Unitárias"
                                value={registro.caixasUnitarias ?? ""}
                                onChange={(e) => alterarCampo("caixasUnitarias", e.target.value)}
                            />

                            <Input
                                label="Valor Total"
                                value={registro.valorTotal ?? ""}
                                onChange={(e) => alterarCampo("valorTotal", e.target.value)}
                                disabled />

                            <Input
                                label="Cubos"
                                value={registro.cubos ?? ""}
                                onChange={(e) => alterarCampo("cubos", e.target.value)}
                            />

                        </div>
                    )}
                </Modal>
            </main>
        </div>
    );
}
